import { Page, View, Text, Image } from '@react-pdf/renderer';
import { LaudoHeader, DadosPedido, DadosImovel, ResumoAvaliacao, Footer } from '../../../types';
import { AVMPageFooter } from './components';
import { StyleSheet } from '@react-pdf/renderer';

const p1 = StyleSheet.create({
  page: {
    width: 595,
    height: 842,
    backgroundColor: '#FFFFFF',
    flexDirection: 'column',
    fontFamily: 'Helvetica',
  },
  content: {
    flex: 1,
    padding: 16,
    flexDirection: 'column',
    gap: 16,
  },
  // Header bar
  headerBar: {
    paddingBottom: 8,
    borderBottom: '1 solid #D6DBE7',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // Section header
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F6F9',
    gap: 8,
    paddingRight: 8,
  },
  accent: {
    width: 3,
    height: 28,
    backgroundColor: '#0090FF',
  },
  sectionTitle: {
    color: '#000000',
    fontSize: 9,
    fontWeight: 'bold',
  },
  // Data row
  dataRow: {
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderBottom: '1 solid #D6DBE7',
  },
  // Field
  label: {
    color: '#000000',
    fontSize: 7.5,
    fontWeight: 'normal',
  },
  value: {
    color: '#000000',
    fontSize: 9,
    fontWeight: 'bold',
  },
  // Resumo dark box
  darkBox: {
    paddingHorizontal: 8,
    paddingVertical: 6,
    backgroundColor: '#001F3F',
    flexDirection: 'column',
    gap: 2,
  },
  darkLabel: {
    color: '#FFFFFF',
    fontSize: 7.5,
    fontWeight: 'normal',
  },
  darkValue: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
});

// Reusable field component
function F({ label, val, width, flex, paddingRight }: { label: string; val: string | number; width?: number; flex?: number; paddingRight?: number }) {
  const safe = val === '' || val === undefined || val === null ? '-' : val;
  const containerStyle: any = { flexDirection: 'column', gap: 2, overflow: 'hidden' };
  if (width) containerStyle.width = width;
  else containerStyle.flex = flex ?? 1;
  if (paddingRight !== undefined) containerStyle.paddingRight = paddingRight;
  return (
    <View style={containerStyle}>
      <Text style={p1.label}>{label}</Text>
      <Text style={p1.value}>{safe}</Text>
    </View>
  );
}

interface Page1IdentificacaoProps {
  header: LaudoHeader;
  dadosPedido: DadosPedido;
  dadosImovel: DadosImovel;
  resumoAvaliacao: ResumoAvaliacao;
  streetViewImageBase64?: string;
  footer: Footer;
  logoBase64?: string;
}

export function Page1Identificacao({
  header,
  dadosPedido,
  dadosImovel,
  resumoAvaliacao,
  streetViewImageBase64,
  footer,
  logoBase64,
}: Page1IdentificacaoProps) {
  return (
    <Page size="A4" style={p1.page}>
      <View style={p1.content}>
        {/* ── HEADER BAR ── */}
        <View style={{ flexDirection: 'column', gap: 8 }}>
          <View style={p1.headerBar}>
            {logoBase64 ? (
              <Image src={logoBase64} style={{ width: 64, height: 24, objectFit: 'contain' }} />
            ) : (
              <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#101010' }}>bari.</Text>
            )}
            <View style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <Text style={{ textAlign: 'right', color: '#000000', fontSize: 10, fontWeight: 'bold' }}>LAUDO DE AVALIAÇÃO</Text>
              <Text style={{ textAlign: 'right', color: '#000000', fontSize: 9 }}>{header.idLaudo}  ·  {header.formatoLaudo}</Text>
            </View>
          </View>
        </View>

        {/* ── SECTIONS ── gap-6 = 24px */}
        <View style={{ flexDirection: 'column', gap: 24 }}>

          {/* ── DADOS DO PEDIDO ── */}
          <View style={{ flexDirection: 'column' }}>
            <View style={p1.sectionHeader}>
              <View style={p1.accent} />
              <Text style={p1.sectionTitle}>DADOS DO PEDIDO</Text>
            </View>
            {/* Row 1: Solicitante(w-44=176), N°Proposta(w-28=112), Data(flex), Tipo(flex) */}
            <View style={p1.dataRow}>
              <F label="Solicitante" val={dadosPedido.solicitanteNome} width={176} />
              <F label="N° da Proposta" val={dadosPedido.propostaNumero} width={112} />
              <F label="Data de solicitação" val={dadosPedido.dataSolicitacao} />
              <F label="Tipo de laudo" val="Sem Vistoria (AVM)" />
            </View>
            {/* Row 2: Empresa(w-44=176), CREA(w-28=112), Avaliador(flex), filler(flex) */}
            <View style={p1.dataRow}>
              <F label="Empresa Responsável" val={dadosPedido.empresaResponsavelNome} width={176} />
              <F label="CREA" val={dadosPedido.avaliadorCrea} width={112} />
              <F label="Avaliador Responsável" val={dadosPedido.avaliadorResponsavelNome} />
              <View style={{ flex: 1 }} />
            </View>
            {/* Row 3: Proponente(w-44=176), CPF(w-28=112), 2 fillers */}
            <View style={p1.dataRow}>
              <F label="Proponente" val={dadosPedido.proponenteNome} width={176} />
              <F label="CPF/CNPJ" val={dadosPedido.proponenteDocumento} width={112} />
              <View style={{ flex: 1 }} />
              <View style={{ flex: 1 }} />
            </View>
          </View>

          {/* ── DADOS DO IMÓVEL ── */}
          <View style={{ flexDirection: 'column' }}>
            <View style={p1.sectionHeader}>
              <View style={p1.accent} />
              <Text style={p1.sectionTitle}>DADOS DO IMÓVEL</Text>
            </View>
            {/* Row 1: Endereço(w-44=176), Número(w-28=112), Complemento(flex), CEP(flex) */}
            <View style={p1.dataRow}>
              <F label="Endereço" val={dadosImovel.enderecoRua || '-'} width={176} />
              <F label="Número" val={dadosImovel.enderecoNumero || 'S/N'} width={112} />
              <F label="Complemento" val={dadosImovel.enderecoComplemento || '-'} />
              <F label="CEP" val={dadosImovel.enderecoCep} />
            </View>
            {/* Row 2: Bairro(w-44=176), UF(w-28=112), Município(flex), Tipo(flex) */}
            <View style={p1.dataRow}>
              <F label="Bairro" val={dadosImovel.enderecoBairro} width={176} />
              <F label="UF" val={dadosImovel.enderecoUf} width={112} />
              <F label="Município" val={dadosImovel.enderecoMunicipio} />
              <F label="Tipo do imóvel" val={dadosImovel.tipoImovel} />
            </View>
            {/* Row 3: IPTU(w-44=176), N°Reg(w-28=112), Tipo(flex), Matrícula(flex) */}
            <View style={p1.dataRow}>
              <F label="IPTU" val={dadosImovel.iptuNumero} width={176} />
              <F label="N° Reg. Imóveis" val={dadosImovel.matriculaRegistroImoveis} width={112} paddingRight={2} />
              <F label="Tipo do imóvel" val={dadosImovel.tipoImovel} />
              <F label="Matrícula" val={dadosImovel.matriculaNumero} />
            </View>
          </View>

          {/* ── RESUMO DA AVALIAÇÃO ── */}
          <View style={{ flexDirection: 'column' }}>
            <View style={p1.sectionHeader}>
              <View style={p1.accent} />
              <Text style={p1.sectionTitle}>RESUMO DA AVALIAÇÃO</Text>
            </View>
            {/* Image (w-72=288) + Fields (flex-1) */}
            <View style={{ flexDirection: 'row' }}>
              {/* Image: w-72 self-stretch */}
              <View style={{ width: 288, backgroundColor: '#E7E7E7', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                {streetViewImageBase64 ? (
                  <Image
                    src={streetViewImageBase64}
                    style={{ width: 288, height: 220, objectFit: 'cover' }}
                  />
                ) : (
                  <Text style={{ color: '#000000', fontSize: 9 }}>[ Imagem do Imóvel ]</Text>
                )}
              </View>
              {/* Fields: flex-1 */}
              <View style={{ flex: 1, flexDirection: 'column' }}>
                {/* Each field: px-2(8) py-1.5(6) border-b gap-0.5(2) */}
                <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                  <Text style={p1.label}>Metodologia</Text>
                  <Text style={p1.value}>{resumoAvaliacao.metodologiaAplicada}</Text>
                </View>
                <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                  <Text style={p1.label}>Grau de Fundamentação</Text>
                  <Text style={p1.value}>{resumoAvaliacao.grauFundamentacao}</Text>
                </View>
                <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                  <Text style={p1.label}>Grau de Precisão</Text>
                  <Text style={p1.value}>{resumoAvaliacao.grauPrecisao}</Text>
                </View>
                <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                  <Text style={p1.label}>Área Privativa</Text>
                  <Text style={p1.value}>{resumoAvaliacao.areaPrivativaTotal}</Text>
                </View>
                {resumoAvaliacao.fatorLiquidez && (
                  <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                    <Text style={p1.label}>Liquidez</Text>
                    <Text style={p1.value}>{resumoAvaliacao.fatorLiquidez}</Text>
                  </View>
                )}
                <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                  <Text style={p1.label}>Valor unitário (R$/m²)</Text>
                  <Text style={p1.value}>{resumoAvaliacao.valorUnitarioFinalM2}</Text>
                </View>
                <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                  <Text style={p1.label}>Somente Área averbada</Text>
                  <Text style={p1.value}>{resumoAvaliacao.valorSomenteAreaAverbada || resumoAvaliacao.valorFinalGarantia}</Text>
                </View>
                {/* Dark box: px-2(8) py-1.5(6) gap-0.5(2) */}
                <View style={p1.darkBox}>
                  <Text style={p1.darkLabel}>Valor para efeito de garantia</Text>
                  <Text style={p1.darkValue}>{resumoAvaliacao.valorFinalGarantia}</Text>
                </View>
              </View>
            </View>
          </View>

        </View>
      </View>

      <AVMPageFooter footer={footer} />
    </Page>
  );
}
